import { useEffect, useMemo, useState } from 'react';
import { PREVIEW_REPOSITORY_FILES } from '@/lib/desktop-preview-data';
import {
  ignoredNames,
  listRepositoryFiles,
  matchRepositoryFiles,
  parseGitStatusFiles,
  type RepositoryFile,
} from '@/lib/desktop-shell/repository-files';
import { useDesktopShellStore } from '@/lib/desktop-shell/store-context';
import { resolveWorkspaceClient } from '@/lib/workspaces/workspace-client';

type RepositoryIndex = { changed: RepositoryFile[]; all: RepositoryFile[]; at: number };

/** A repository's files are read again after this long (what the agent wrote shows up). */
const FRESH_MS = 30_000;
const indexes = new Map<string, RepositoryIndex>();
const loading = new Map<string, Promise<RepositoryIndex>>();

async function loadIndex(
  key: string,
  workspace: string,
  cloudRuntime: Parameters<typeof resolveWorkspaceClient>[0],
): Promise<RepositoryIndex> {
  const pending = loading.get(key);
  if (pending !== undefined) return pending;
  const load = (async () => {
    const client = resolveWorkspaceClient(cloudRuntime, workspace);
    try {
      await client.ready;
      const gitignore = await client.readTextFile('.gitignore').catch(() => '');
      const [review, all] = await Promise.all([
        client.gitReview().catch(() => null),
        listRepositoryFiles((path) => client.listDirectory(path), {
          ignored: ignoredNames(gitignore),
        }),
      ]);
      const index = {
        changed: review === null ? [] : parseGitStatusFiles(review.status),
        all,
        at: Date.now(),
      };
      indexes.set(key, index);
      return index;
    } finally {
      client.close();
      loading.delete(key);
    }
  })();
  loading.set(key, load);
  return load;
}

/**
 * The files of the conversation's repository matching an @ query (`null` while @ is not open):
 * its changed files first, then the others by name. Read once per half minute per repository.
 */
export function useRepositoryFiles(query: string | null, preview: boolean): RepositoryFile[] {
  const workspace = useDesktopShellStore((state) => state.workspace);
  const personalWorkspaceRoot = useDesktopShellStore((state) => state.personalWorkspaceRoot);
  const cloudRuntime = useDesktopShellStore((state) => state.cloudRuntime);
  const repository = workspace !== null && workspace !== personalWorkspaceRoot ? workspace : null;
  const key =
    repository === null ? null : `${cloudRuntime === null ? 'local' : 'cloud'}:${repository}`;
  const [index, setIndex] = useState<RepositoryIndex | null>(() =>
    key === null ? null : (indexes.get(key) ?? null),
  );
  const open = query !== null;
  useEffect(() => {
    if (!open || preview || key === null || repository === null) return;
    const cached = indexes.get(key);
    if (cached !== undefined) setIndex(cached);
    if (cached !== undefined && Date.now() - cached.at < FRESH_MS) return;
    let active = true;
    void loadIndex(key, repository, cloudRuntime)
      .then((next) => {
        if (active) setIndex(next);
      })
      // No workspace server yet (or the sandbox is starting): @ offers the other references.
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [cloudRuntime, key, open, preview, repository]);
  return useMemo(() => {
    if (query === null) return [];
    if (preview) return matchRepositoryFiles(PREVIEW_REPOSITORY_FILES, [], query);
    if (index === null || repository === null) return [];
    return matchRepositoryFiles(index.changed, index.all, query);
  }, [index, preview, query, repository]);
}
